/**
 * cypAdhdSsiDomainContent.js — clinician-facing content for the SSI's three
 * functional-impairment domains: a clinical tooltip, the suggested interview
 * prompts, and grouped checklists of example manifestations.
 *
 * Transcribed from the clinical functional-impairment content supplied at the
 * HK testing session (2 Sept). Wording is verbatim except:
 *  - "DSM-V" normalised to "DSM-5" (the platform's spelling throughout);
 *  - "masking" replaced with "camouflaging" per the design rules' clinical
 *    language table (masking is the clinically wrong term for children) —
 *    flagged for Tom's clinical review;
 *  - trailing full stops on checklist items normalised away, as in
 *    cypAdhdSsiContent.js (they render as checkbox labels, not sentences);
 *  - the doc's "Functional impairment / Level of impairment" checkbox rows
 *    are not repeated here: the panel's "At least moderate impairment:
 *    Yes/No" decision already carries that threshold (decision, Sai 26 Aug).
 *
 * This lives in its OWN file, not cypAdhdSsiContent.js: that file's content
 * is hashed into the generated Python mirror (ssi_content.py), and domain
 * content has no backend consumer, so it must not disturb that contract.
 *
 * Keyed by domain key (education / social / psychological). The doc's
 * PERSONAL FUNCTIONING section maps onto the psychological domain
 * ("Emotional Wellbeing and Feelings") — the same Personal/Educational/Social
 * mapping the domain list itself records.
 */

const SHARED_TOOLTIP = 'DSM-5 and ICD-11 both require that ADHD symptoms cause a direct negative impact on functioning, typically in academic, occupational, or social domains. NICE expands this to include personal, educational, occupational, and social functioning. Functional impairment should be evaluated in relation to the child’s developmental level and across multiple settings. The absence of obvious functional failure does not rule out ADHD, as camouflaging or compensation may conceal difficulties while increasing exhaustion, emotional strain, or delayed impairment elsewhere.';

const SHARED_PROMPTS = [
  'Can you describe a few specific situations where their challenges have caused a real problem for them, at home, at school, or with friends?',
  'Some children work very hard to hide their difficulties or to keep up with expectations. Are there times when they seem to be coping well on the outside, but it takes a lot of effort, causes fatigue, or leads to difficulties later?',
];

export const CYP_SSI_DOMAIN_CONTENT = {
  education: {
    tooltip: SHARED_TOOLTIP,
    suggestedPrompts: SHARED_PROMPTS,
    exampleGroups: [
      {
        title: 'Learning progress',
        options: [
          'Working below the level expected for their ability',
          'Needs extra time to complete school tasks, homework, or assignments',
          'Maintains academic progress only through exceptionally high effort',
          'Frequently forgets, avoids, loses, or becomes distressed by homework or assignments',
        ],
      },
      {
        title: 'Classroom behaviour and participation',
        options: [
          'Repeated teacher concerns about concentration or classroom behaviour',
          'Disrupts lessons due to difficulty staying seated or focused',
          'Needs instructions repeated because they miss details or drift off',
          'Struggles to organise school materials, or keep track of school or college tasks',
        ],
      },
      {
        title: 'Educational pathway',
        options: [
          'Requires additional learning, behavioural support or small-group teaching',
          'Relies heavily on external structure (visual schedules, structured routines)',
          'Cognitive strengths may partially conceal difficulties, but significant effort is required to maintain expected performance',
          'Excluded or suspended from attending school',
          'Requires part-time timetables, or inability to fully access education',
          'Emotionally based school avoidance',
        ],
      },
    ],
  },
  social: {
    tooltip: SHARED_TOOLTIP,
    suggestedPrompts: SHARED_PROMPTS,
    exampleGroups: [
      {
        title: 'Peer relationships',
        options: [
          'Has fewer close friends than peers',
          'Has difficulty developing or maintaining age-appropriate friendships',
          'Frequently teased, or criticised by peers',
          'Excluded from games, parties, teams, or group activities',
          'Acts aggressively, intrusively, or controlling toward peers leading to conflict',
          'Reacts strongly to disagreements or minor frustrations with peers',
          'Gets bored, overwhelmed, or exhausted during prolonged social interaction',
          'Hesitant to form close friendships or trusting peer relationships because of fear of rejection, conflict, or previous negative experiences',
          'Socially withdrawn, or overly passive, due to repeated social failure experiences',
        ],
      },
      {
        title: 'Social communication',
        options: [
          'Interrupts others or talks over peers without noticing',
          'Frequently argues with peers',
          'Misunderstood by peers due to impulsive comments',
          'Friendships are often short-lived due to conflict, or social behaviour',
          'Struggles to join games, start conversations, or confidently approach peers',
          'Low confidence in social situations due to past negative experiences',
        ],
      },
    ],
  },
  psychological: {
    tooltip: SHARED_TOOLTIP,
    suggestedPrompts: SHARED_PROMPTS,
    exampleGroups: [
      {
        title: 'Free time and hobbies',
        options: [
          'Finds it hard to relax during free time and often stays “on the go”',
          'Needs constant physical activity (or to be busy) to feel calm or settled',
          'Frequently gets hurt during play or sports',
          'Cannot enjoy a whole film, story, or quiet activity in one sitting',
          'Becomes overtired because they are overactive',
          'Rapidly loses interest in hobbies or activities',
          'Seeks excitement by taking dangerous dares or rule-breaking behaviours',
          'Frequent bumps, falls, accidents, or injuries',
          'Eats quickly or struggles to stop eating once started',
        ],
      },
      {
        title: 'Self-confidence and self-image',
        options: [
          'Feels unsure of themselves after repeated corrections or negative comments',
          'Thinks they are “not good” at things due to difficulties',
          'Avoids trying new activities because they worry about making mistakes',
          'Reacts strongly to criticism, feedback, or perceived unfairness',
          'Tries very hard to make things perfect and becomes upset when they can’t',
          'Notices they are “different” from peers and feels upset, or confused by their own behaviour or difficulties',
        ],
      },
      {
        title: 'Family and home relationships',
        options: [
          'Regular conflict with siblings and parents/carers',
          'Receives repeated sanctions, or behavioural consequences',
          'Avoids family activities',
          'Strained family relationships',
          'Needs high level of supervision, prompting, or behavioural scaffolding',
        ],
      },
    ],
  },
};
